import { Injectable, computed, effect, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DialogService } from '@ms/core/components/dialog/service/dialog.service';
import { IUserProfile } from 'app/layout/model/profile.model';
import { SharedConstants } from 'app/main/shared/shared.constants';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { phoneMaskConstants } from '..';

import { Language } from 'app/main/shared/model/translateble-string.model';
import { equalPrimitives } from 'app/main/shared/utils';
import { environment } from 'environments/environment';
import { SessionState, SessionStoreService, createInitialSessionState } from '../components/login/state/session.store';
import { IUser, UserUI } from '../model/user.model';
import { EventManager } from '../service/event-manager.service';
import { UserService } from '../service/user.service';
import { AccountService } from './account.service';

@Injectable({ providedIn: 'root' })
export class PrincipalService {
    _loginCompleted = signal<boolean>(false);
    _accessToken = signal<string>(null);
    accessToken$ = toObservable(this._accessToken);
    state = signal<SessionState>(createInitialSessionState(), {
        equal: equalPrimitives,
    });
    state$ = toObservable(this.state);
    unreadMessageCount = signal<number>(0);
    unreadMessageCountLoading = signal<boolean>(false);
    unreadAnnouncementCount = signal<number>(0);
    unreadCounterLoaded = signal<boolean>(false);
    announcementLoading = signal<boolean>(false);
    selectedDashboard = signal<'HEAD_TEACHER' | 'INCIDENT_MANAGER' | 'HEALTH_MANAGER' | 'COUNSELOR' | null | string>(null);

    incidentCount = signal<number>(0);
    incidentLoading = signal<boolean>(false);
    incidentLoaded = signal<boolean>(false);
    isUSA = computed(() => {
        return this.state().authenticated ? this.state().user?.activeSchool?.language == Language.en_us : false;
    });

    isAlertAvailable = computed(() => {
        return this.state().user?.ui != UserUI.STUDENT && this.state().user?.ui != UserUI.PARENT;
    });

    isGoogleClassroomAuthenticated = signal<boolean>(false);
    isGoogleClassroomAuthenticated$ = toObservable(this.isGoogleClassroomAuthenticated);
    constructor(
        private account: AccountService,
        private userService: UserService,
        private eventManager: EventManager,
        private _sessionStoreService: SessionStoreService,
        private cookieService: CookieService,
        private dialogService: DialogService
    ) {
        effect(() => {
            const account = this.state();
            //debugger;
            console.log('User signal state changed', { ...account });
            this._sessionStoreService.set(account.authenticated, account.user, account.eventFire);
            if (account.authenticated) {
                if (this.selectedDashboard()) {
                    this.cookieService.set(account.user.username + '_dashboard', this.selectedDashboard());
                } else {
                    const dashboard = this.cookieService.get(account.user.username + '_dashboard');
                    if (dashboard?.length > 0) {
                        this.selectedDashboard.set(dashboard);
                    }
                }
            }
        });
    }

    get authenticatedUser(): IUser {
        return this.state().user;
    }

    get phoneMask() {
        const user = this.state().user;
        if (user.activeSchool?.phoneMask) {
            const phoneMask = phoneMaskConstants.find(s => s.id == user.activeSchool.phoneMask);
            if (phoneMask?.mask) {
                return phoneMask;
            } else {
                return phoneMaskConstants.find(m => m.id == 'mask3');
            }
        }
    }

    get isAuthenticated(): boolean {
        return this.state().authenticated;
    }

    set refreshedApp(value: boolean) {
        this.cookieService.set('campus_refreshedApp', JSON.stringify(value));
    }

    get refreshedApp() {
        const refreshedApp = this.cookieService.get('campus_refreshedApp');
        return 'true' == refreshedApp;
    }

    set loggedIn(value: boolean) {
        this.cookieService.set('campus_loggedIn', JSON.stringify(value));
    }

    get loggedIn() {
        const loginCookie = this.cookieService.get('campus_loggedIn');
        return 'true' == loginCookie;
    }

    get accessToken() {
        return this._accessToken();
    }

    set accessToken(value: string) {
        this._accessToken.update(() => value);
        if (value) {
            sessionStorage.setItem('access_token', value);
        } else {
            sessionStorage.removeItem('access_token');
        }
    }

    getRoles(): string[] {
        if (!this.isAuthenticated) {
            return;
        }
        return this.authenticatedUser.roles;
    }

    hasAnyAuthority(roles: string[] | string): Promise<boolean> {
        return Promise.resolve(this.hasAnyAuthorityDirect(roles));
    }

    hasAnyAuthorityDirect(roles: string[] | string): boolean {
        if (!this.isAuthenticated) {
            return false;
        }
        const userRoles = this.authenticatedUser.roles;
        if (Array.isArray(roles)) {
            return roles.some(role => userRoles?.includes(role));
        } else {
            return userRoles?.includes(roles);
        }
    }

    hasAuthority(authority: string): boolean {
        return this.isAuthenticated && this.authenticatedUser.roles?.includes(authority);
    }

    hasPermission(id: string): boolean {
        const userIdentity = this.authenticatedUser;
        if (!userIdentity) return false;

        const isAdmin = userIdentity.roles?.some(role =>
            [
                SharedConstants.ROLE_ADMIN,
                SharedConstants.ROLE_MANAGER,
                SharedConstants.ROLE_SYSTEM,
                SharedConstants.ROLE_TENANT_ADMIN,
            ].includes(role)
        );
        return isAdmin || userIdentity.privileges?.some(privilege => privilege.id === id);
    }

    isNotHiddenItem(id: string): boolean {
        const userIdentity = this.authenticatedUser;
        if (!userIdentity) return false;

        const permission = this.hasPermission(id);
        if (!permission) return false;

        const hiddenPortletProperty = userIdentity.properties?.find(p => p.key === 'HIDDEN_PORTLETS');
        return !hiddenPortletProperty || !hiddenPortletProperty.value.split(',').includes(id);
    }

    getProperty(key: string): string | undefined {
        const userIdentity = this.authenticatedUser;
        return userIdentity?.properties?.find(p => p.key === key)?.value;
    }

    closeSession() {
        this.state.set(createInitialSessionState());
        //console.log('CLOSE-SESSION', sessionStorage.getItem('currentUser'));
        this.unreadMessageCount.set(null);
        this.unreadMessageCountLoading.set(false);
        this.unreadAnnouncementCount.set(0);
        this.unreadCounterLoaded.set(false);
        this.announcementLoading.set(false);
        this.incidentCount.set(null);
        this.incidentLoaded.set(false);
        this.incidentLoading.set(false);
        this.isGoogleClassroomAuthenticated.set(false);
        //this._sessionStore.clear();
        this.refreshedApp = false;
        this.accessToken = null;
        environment.timezoneOffset = -1;
    }

    identity(): Promise<IUser> {
        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        return firstValueFrom(this.account.get())
            .then(response => {
                const account = response.body;
                console.log('Authenticated Image Loaded', account);
                this.initUserAuth(true, true, account);
                return account;
            })
            .catch(err => {
                console.error('Login error', err);
                this.eventManager.broadcast({ name: 'show-login', content: {} });
                this.dialogService.error(err.error?.message);
                return null;
            });
    }

    initUserAuth(authenticated: boolean, refreshSchoolList: boolean, account: IUser) {
        if (authenticated && account) {
            const langExist = this.cookieService.check(`${account.id}_lang`);
            if (langExist) {
                const lang = this.cookieService.get(`${account.id}_lang`);
                console.log('Lang', lang);
                account.langKey = lang;
            }
            if (!account.imageUrl || !account.image) {
                this.emptyAvatar(account);
            }
            this.state.update(state => {
                return { ...state, authenticated: true, user: account, eventFire: true };
            });
            console.log('Authenticated Image Loaded', account);

            // Layout created and initiated before this event
            let time = 0;
            if (account.ui == UserUI.DEFAULT) {
                time = 500;
            }
            setTimeout(() => {
                this.eventManager.broadcast({
                    name: 'after-login-event',
                    account,
                    refreshSchoolList,
                });
            }, time);
        } else {
            this._sessionStoreService.clear();
        }
    }

    imageLoad(user: IUser) {
        if (user?.imageUrl) {
            this.userService.getImagePath(user.username).subscribe(
                image => {
                    console.log('LoadImage', user, image);
                    if (image?.data && user.imageUrl) {
                        user.image = image.data;
                    } else {
                        this.emptyAvatar(user);
                    }
                    // this method is async call !!!
                    const account = { ...this.authenticatedUser };
                    account.image = user.image;
                    this.state.update(state => {
                        return { ...state, authenticated: true, user: account, eventFire: true };
                    });
                },
                () => {
                    this.emptyAvatar(user);
                }
            );
        } else {
            this.emptyAvatar(user);
            this.state.update(state => {
                return { ...state, authenticated: true, user, eventFire: true };
            });
        }
    }

    emptyAvatar(user: IUser) {
        console.log('LoadImage');
        user.imageUrl = './assets/images/avatars/avatar.avif';
        user.image = './assets/images/avatars/avatar.avif';
    }

    isIdentityResolved(): boolean {
        const user = this.authenticatedUser;
        return user != undefined && user != null;
    }

    getImageUrl(): string {
        const user = this.authenticatedUser;
        return user ? user.imageUrl : null;
    }

    getImage() {
        const user = this.authenticatedUser;
        return user ? user.image : undefined;
    }

    get profile(): IUserProfile {
        return this._sessionStoreService.profile;
    }

    updateUserState(user: IUser, schoolChanged: boolean) {
        this.state.update(state => {
            return { ...state, user, eventFire: true };
        });
        if (schoolChanged) {
            this.eventManager.broadcast({
                name: 'active-school-changed',
                content: user,
            });
        }
    }

    updateLanguage(langKey: string) {
        this.state.update(state => {
            return { ...state, user: { ...state.user, langKey }, eventFire: true };
        });
    }

    isEnabledPush(): boolean {
        const user = this.authenticatedUser;
        if (user) {
            const property = user.properties.find(p => p.key == 'WEB_PUSH_endpoint');
            return property?.key?.length > 0;
        }
        return false;
    }

    removeWebPush() {
        const user = this.authenticatedUser;
        if (user) {
            const properties = user.properties.filter(p => !p.key.startsWith('WEB_PUSH_'));
            user.properties = properties;
            this.account.get().subscribe(account => {
                console.log('WEB_PUSH REMOVED');
                const changedUser = account.body;
                changedUser.activeSchool = user.activeSchool;
                changedUser.schools = user.schools;
                changedUser.academicYear = user.academicYear;
                changedUser.image = user.image;
                this.state.update(state => {
                    return { ...state, authenticated: true, user: changedUser, eventFire: true };
                });
            });
        }
    }

    addWebPush(sub: PushSubscription) {
        const user = this.authenticatedUser;
        if (user) {
            this.userService
                .createProperties(user.id, [
                    {
                        key: 'WEB_PUSH_endpoint',
                        value: sub.toJSON().endpoint,
                        active: true,
                        locked: true,
                    },
                    {
                        key: 'WEB_PUSH_KEYS_p256dn',
                        value: sub.toJSON().keys.p256dh,
                        active: true,
                        locked: true,
                    },
                    {
                        key: 'WEB_PUSH_KEYS_auth',
                        value: sub.toJSON().keys.auth,
                        active: true,
                        locked: true,
                    },
                ])
                .subscribe(() => {
                    console.log('WEB_PUSH CREATED');
                    this.account.get().subscribe(account => {
                        const changedUser = account.body;
                        changedUser.activeSchool = user.activeSchool;
                        changedUser.schools = user.schools;
                        changedUser.image = user.image;
                        changedUser.academicYear = user.academicYear;
                        this.state.update(state => {
                            return { ...state, authenticated: true, user: changedUser, eventFire: true };
                        });
                    });
                });
        }
    }
}
